<script lang="ts">
  import { Eye, EyeClosed, GripVertical, Link2Off } from '@lucide/svelte'
  import IconButton from '../../icon-button/IconButton.svelte'
  import ContextMenu from '../../context-menu/ContextMenu.svelte'
  import ContextMenuTrigger from '../../context-menu/ContextMenuTrigger.svelte'
  import type { MouseEventHandler } from 'svelte/elements'
  import useCanvasSelection from '../../../hooks/canvas-selection'
  import FileContextMenuContent from '../../file-context-menu-content/FileContextMenuContent.svelte'
  import { getGroupAssignedPrinterId } from '../../../helpers/group'
  import type { Object3D } from 'three'
  import useObjectQueries from '../../../routes/project/hooks/useObjectQueries'
  import usePrintersQueries from '../../../routes/project/hooks/usePrintersQueries'
  import { ThreeItemKeys } from '../../../constants/three'
  import Color from '../../color-picker/Color.svelte'
  import { queryReactiveAccessor } from '../../../helpers/tanstack-query'

  const {
    fileId,
    hidePrinterIndicator,
    isDraggable = false,
    noPadding,
  } = $props<{
    fileId: Object3D['uuid']
    hidePrinterIndicator?: boolean
    isDraggable?: boolean
    noPadding?: boolean
  }>()

  const { object, multiSelection, upsert, hasSingleSoloGroup, getSelectedFileIds } = useCanvasSelection()

  const { useObject, showMutation, hideMutation, focusCamera }
    = useObjectQueries()

  const { usePrinters } = usePrintersQueries()
  const printersData = usePrinters()

  /* svelte-ignore state_referenced_locally */
  const fileQuery = useObject(fileId)
  let file = $derived.by(queryReactiveAccessor(fileQuery))
  let fileUuid = $derived.by(queryReactiveAccessor(fileQuery, data => data?.uuid))
  let assignedPrinterId = $derived.by(
    queryReactiveAccessor(
      fileQuery,
      data => data?.userData[ThreeItemKeys.ASSIGNED_PRINTER_ID],
    ),
  )
  let visible = $derived.by(
    queryReactiveAccessor(fileQuery, data => data?.visible),
  )

  let selected = $derived(
    $object?.uuid === fileUuid
      || (hasSingleSoloGroup() && getSelectedFileIds()[0] === fileUuid)
      || $multiSelection.some(item => item.uuid === fileUuid),
  )
  let selectedPrinter = $derived(
    printersData.data?.find(
      item =>
        item.userData[ThreeItemKeys.PRINTER_ID] == assignedPrinterId,
    ),
  )

  function toggleHidden() {
    if (visible) {
      hideMutation.mutate(fileId)
    }
    else {
      showMutation.mutate(fileId)
    }
  }

  const addFileToSelection: MouseEventHandler<HTMLButtonElement> = (event) => {
    if (!file) return

    upsert(file, event && (event.ctrlKey || event.metaKey))
  }
</script>

{#if file && fileUuid}
  <ContextMenu>
    <ContextMenuTrigger>
      <div
        class={[
          'container',
          { selected, hidden: !visible, noPadding },
        ]}
      >
        {#if isDraggable}
          <IconButton size={1.5}>
            <GripVertical size={16} />
          </IconButton>
        {/if}
        <button
          class="name"
          onclick={addFileToSelection}
          ondblclick={(e) => {
            addFileToSelection(e)
            focusCamera(fileUuid)
          }}
        >
          {#if !hidePrinterIndicator}
            <div class="color-indicator">
              {#if selectedPrinter}
                <Color
                  color={selectedPrinter?.userData[
                    ThreeItemKeys.PRINTER_COLOR
                  ]}
                />
              {:else}
                <Link2Off
                  color="var(--right-bar-muted-foreground)"
                  size=".75rem"
                />
              {/if}
            </div>
          {/if}
          {file?.name}
        </button>
        <IconButton onclick={toggleHidden}>
          {#if visible}
            <Eye />
          {:else}
            <EyeClosed />
          {/if}
        </IconButton>
      </div>
    </ContextMenuTrigger>
    <FileContextMenuContent
      assignedPrinterId={getGroupAssignedPrinterId(file)}
      fileId={fileUuid}
    />
  </ContextMenu>
{/if}

<style lang="scss">
  .container {
    background-color: var(--printer-pane-background);
    display: flex;
    flex-direction: row;
    gap: 0.25rem;
    align-items: center;
    justify-content: space-between;
    padding: 0 0.5rem 0 2rem;

    &:hover,
    &.selected {
      background-color: var(--printer-pane-active-background);
    }

    &.noPadding {
      padding: 0 0.5rem 0 1rem;
    }

    &.hidden {
      .name {
        opacity: 0.3;
      }
    }

    :global(input) {
      font-size: 0.75rem;
      height: inherit;
    }

    .name {
      align-items: center;
      background-color: transparent;
      border: none;
      color: currentColor;
      cursor: pointer;
      display: flex;
      flex-direction: row;
      flex: 1;
      font-size: 0.75rem;
      gap: 0.25rem;
      outline: none;
      overflow: hidden;
      padding: 0;
      text-align: start;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-height: 2rem;

      .color-indicator {
        align-items: center;
        display: flex;
        height: 1rem;
        justify-content: center;
        width: 1rem;
      }

      &:focus-visible {
        outline: 1px solid var(--stroke);
        outline-offset: -1px;
      }
    }

    :global(.head) {
      justify-content: space-between;
    }

    :global(.head[data-hidden="true"]) {
      filter: opacity(0.5);
    }

    :global(.head-container:has([data-selected="true"])) {
      background-color: var(--printer-pane-active-background);
    }
  }
</style>
